import React from "react";
import { Box, Typography, IconButton, CircularProgress, Tooltip } from "@mui/material";
import {
	CheckCircle as CheckCircleIcon,
	Warning as WarningIcon,
	Close as CloseIcon,
	InsertDriveFile as FileIcon,
	Image as ImageIcon,
	PictureAsPdf as PdfIcon,
	Description as DocIcon,
	TableChart as SpreadsheetIcon,
} from "@mui/icons-material";

const FILE_STATUS = {
	UPLOADING: "UPLOADING",
	SCANNING: "SCANNING",
	READY: "READY",
	QUARANTINED: "QUARANTINED",
	SCAN_FAILED: "SCAN_FAILED",
};

function getFileIcon(mimeType) {
	if (!mimeType) return <FileIcon sx={{ fontSize: 18, color: "#666" }} />;
	if (mimeType.startsWith("image/")) return <ImageIcon sx={{ fontSize: 18, color: "#4CAF50" }} />;
	if (mimeType === "application/pdf") return <PdfIcon sx={{ fontSize: 18, color: "#F44336" }} />;
	if (mimeType.includes("spreadsheet") || mimeType === "text/csv") return <SpreadsheetIcon sx={{ fontSize: 18, color: "#4CAF50" }} />;
	if (mimeType.includes("wordprocessingml") || mimeType === "text/plain" || mimeType === "text/markdown") return <DocIcon sx={{ fontSize: 18, color: "#2196F3" }} />;
	return <FileIcon sx={{ fontSize: 18, color: "#666" }} />;
}

function formatFileSize(bytes) {
	if (!bytes) return "";
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getStatusTooltip(status, msg) {
	switch (status) {
		case FILE_STATUS.UPLOADING: return msg("file.uploading", "Uploading...");
		case FILE_STATUS.SCANNING: return msg("file.scanning", "Scanning for viruses...");
		case FILE_STATUS.READY: return msg("file.ready", "Ready to send");
		case FILE_STATUS.QUARANTINED: return msg("file.quarantined", "File blocked — potential threat detected");
		case FILE_STATUS.SCAN_FAILED: return msg("file.scanFailed", "Scan failed — please try again");
		default: return "";
	}
}

export default function FileChip({ file, onRemove, msg, disabled }) {
	const { originalName, sizeBytes, mimeType, scanStatus } = file;
	const isTerminal = [FILE_STATUS.READY, FILE_STATUS.QUARANTINED, FILE_STATUS.SCAN_FAILED].includes(scanStatus);
	const isError = [FILE_STATUS.QUARANTINED, FILE_STATUS.SCAN_FAILED].includes(scanStatus);
	const isProcessing = [FILE_STATUS.UPLOADING, FILE_STATUS.SCANNING].includes(scanStatus);

	return (
		<Tooltip title={getStatusTooltip(scanStatus, msg)} arrow placement="top">
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 0.75,
					padding: "4px 8px",
					borderRadius: "8px",
					backgroundColor: isError ? "#FFF3F3" : "#F5F5F5",
					border: `1px solid ${isError ? "#FFCDD2" : "#E0E0E0"}`,
					maxWidth: "180px",
					position: "relative",
				}}
			>
				{getFileIcon(mimeType)}

				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography
						variant="caption"
						sx={{
							display: "block",
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							fontSize: "11px",
							fontWeight: 500,
							color: isError ? "#D32F2F" : "#333",
							lineHeight: 1.3,
						}}
					>
						{originalName}
					</Typography>
					<Typography
						variant="caption"
						sx={{ fontSize: "10px", color: "#999", lineHeight: 1.2 }}
					>
						{formatFileSize(sizeBytes)}
					</Typography>
				</Box>

				{isProcessing && (
					<CircularProgress size={14} thickness={5} sx={{ color: "#B26EFC" }} />
				)}
				{scanStatus === FILE_STATUS.READY && (
					<CheckCircleIcon sx={{ fontSize: 14, color: "#4CAF50" }} />
				)}
				{isError && (
					<WarningIcon sx={{ fontSize: 14, color: "#F44336" }} />
				)}

				{onRemove && !disabled && (
					<IconButton
						size="small"
						onClick={() => onRemove(file.fileId)}
						sx={{
							padding: "2px",
							position: "absolute",
							top: -6,
							right: -6,
							backgroundColor: "#fff",
							border: "1px solid #E0E0E0",
							"&:hover": { backgroundColor: "#F5F5F5" },
						}}
					>
						<CloseIcon sx={{ fontSize: 12 }} />
					</IconButton>
				)}
			</Box>
		</Tooltip>
	);
}

export { FILE_STATUS };
